import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lightning, Check, ArrowRight, ArrowsClockwise, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { billingApi } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    billingApi.plans().then(setPlans).catch(() => setPlans([]));
  }, []);

  const choose = async (plan) => {
    if (!user) {
      navigate(`/signup?plan=${plan.id}`);
      return;
    }
    setBusyId(plan.id);
    try {
      const { url } = await billingApi.startCheckout(plan.id);
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusyId(null);
    }
  };

  const FEATURES_BY_PLAN = {
    starter: [
      "Up to 5 trucks/machines",
      "Built for owner-operators + 1-truck haulers",
      "Live GPS · Bridge Formula compliance on your phone",
      "GPS-watermarked POD photos (tamper-evident)",
      "Auto-email branded receipts to broker/customer",
      "Geofence customer sites · arrival alerts",
    ],
    pro: [
      "Up to 25 trucks/machines",
      "Built for small general-freight fleets",
      "Everything in Starter +",
      "Drag-style dispatch board · driver PWA · QR onboarding",
      "One-tap NPS feedback · customer voice dashboard",
      "Slack alerts when customers leave 👎",
      "Daily PDF reports with POD appendix",
      "Hour-meter tracking · service-due alerts",
    ],
    fleet: [
      "Unlimited trucks/machines · unlimited yards",
      "Built for rental yards + multi-location operators",
      "Everything in Pro +",
      "Multi-yard / multi-tenant isolation · super-admin console",
      "Samsara · PeopleNet · white-label hardware integrations",
      "Per-asset axle overrides · custom permit templates",
      "Priority support · SLA-backed",
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#e4e4e7]" data-testid="pricing-page">
      <nav className="border-b border-[#2a2a2a] px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 bg-[#ff8c00] flex items-center justify-center">
            <Lightning size={20} weight="fill" color="#000" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight">LOAD LOGIC</span>
        </Link>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold">
          {user ? (
            <Link to="/live" className="text-[#a1a1aa] hover:text-white">Back to dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="text-[#a1a1aa] hover:text-white">Sign in</Link>
              <Link to="/signup" className="px-3 py-2 bg-[#ff8c00] text-black hover:bg-[#e67e00]">Start trial →</Link>
            </>
          )}
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold mb-2">· Simple monthly pricing</div>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight mb-3">Pick the plan that fits your fleet.</h1>
          <p className="text-[#a1a1aa] text-base max-w-xl mx-auto">All plans include the full dispatch OS. Pay monthly. Cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#2a2a2a]">
          {plans.map((p) => {
            const popular = p.id === "pro";
            return (
              <div
                key={p.id}
                className="bg-[#0d0d0d] p-6 lg:p-8 relative flex flex-col"
                data-testid={`plan-${p.id}`}
              >
                {popular && (
                  <div className="absolute top-0 right-0 px-3 py-1 bg-[#ff8c00] text-black text-[10px] uppercase tracking-[0.25em] font-black">
                    Most popular
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  {p.id === "fleet" && <Crown size={16} weight="fill" className="text-[#ff8c00]" />}
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">{p.id === "fleet" ? "Enterprise" : "Plan"}</div>
                </div>
                <h2 className="font-heading font-black text-2xl tracking-tight mb-3">{p.name}</h2>
                <div className="mb-4">
                  <span className="font-heading font-black text-5xl tracking-tighter">${Math.round(p.amount)}</span>
                  <span className="text-[#71717a] text-sm ml-2">/ month</span>
                </div>
                <p className="text-xs text-[#a1a1aa] mb-6 leading-relaxed min-h-[40px]">{p.blurb}</p>

                <ul className="space-y-2 mb-6 flex-1">
                  {(FEATURES_BY_PLAN[p.id] || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-[#a1a1aa]">
                      <Check size={12} weight="bold" className="text-[#22c55e] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => choose(p)}
                  disabled={busyId === p.id}
                  className={`w-full py-3 text-[10px] uppercase tracking-[0.25em] font-black flex items-center justify-center gap-2 transition-colors ${
                    popular
                      ? "bg-[#ff8c00] text-black hover:bg-[#e67e00]"
                      : "border border-[#ff8c00] text-[#ff8c00] hover:bg-[#ff8c00] hover:text-black"
                  } disabled:opacity-50`}
                  data-testid={`choose-${p.id}`}
                >
                  {busyId === p.id
                    ? <><ArrowsClockwise size={12} className="animate-spin" /> Redirecting…</>
                    : <>{user ? "Choose " + p.name : "Start trial · " + p.name} <ArrowRight size={12} weight="bold" /></>}
                </button>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10 text-[10px] uppercase tracking-[0.25em] text-[#71717a]">
          14-day free trial · No card required to start · USD pricing · Stripe-secured checkout
        </div>
      </section>
    </div>
  );
}
